// ===========================================
// Email Sequence API
// ===========================================
// Admin-authored drip series: CRUD on the templates, plus the enrolment
// lifecycle (start / pause / resume / cancel) for individual students.
//
// ⚠️ EVERY route here answers with the `{ status, data, message? }` envelope,
// so every call goes through `ApiResponse<T>` and returns `response.data`.
// `apiClient` already unwraps axios, so a call typed `apiClient.get<T[]>(…)`
// hands back the ENVELOPE where the caller expects an array — `tsc` stays green
// and the page dies on `response.map is not a function`. The wrapper is not
// ceremony; it is the thing that keeps that from compiling.

import { apiClient } from '../services/api';
import type {
  ApiMessageOnlyResponse,
  ApiResponse,
  CreateEmailSequenceInput,
  EmailSequence,
  EmailSequenceDeleteResult,
  EmailSequenceEnrolment,
  EmailSequenceStartPreview,
  EmailSequenceStatus,
  EmailSequenceStepPreview,
  StartEmailSequenceInput,
  StartEmailSequencePreviewInput,
  TestEmailSequenceInput,
  UpdateEmailSequenceInput,
} from '../types/emailSequence.types';

const BASE_URL = '/admin/email-sequences';

// ===========================================
// STATUS
// ===========================================

/**
 * Is the sweep permitted to send on this server?
 *
 * ⚠️ The boolean is at `data.enabled`; returning the whole envelope would make
 * the caller read `undefined`, which is falsy — so the "switched off" banner
 * would show permanently, in BOTH flag states, with nothing failing.
 *
 * `false` does not mean broken: an admin can still author series and start
 * students on them, and they begin sending when the flag is turned on.
 */
export async function getEmailSequenceStatus(): Promise<EmailSequenceStatus> {
  const response = await apiClient.get<ApiResponse<EmailSequenceStatus>>(`${BASE_URL}/status`);
  return response.data;
}

// ===========================================
// SEQUENCE CRUD
// ===========================================

/**
 * List sequences. Archived ones are excluded unless asked for.
 *
 * ⚠️ `includeArchived` is compared as the exact STRING `'true'` server-side and
 * is not schema-validated, so it is serialised here explicitly rather than
 * passed as a boolean.
 */
export async function getEmailSequences(includeArchived = false): Promise<EmailSequence[]> {
  const response = await apiClient.get<ApiResponse<EmailSequence[]>>(BASE_URL, {
    params: includeArchived ? { includeArchived: 'true' } : undefined,
  });
  return response.data;
}

export async function getEmailSequence(id: string): Promise<EmailSequence> {
  const response = await apiClient.get<ApiResponse<EmailSequence>>(`${BASE_URL}/${id}`);
  return response.data;
}

export async function createEmailSequence(
  data: CreateEmailSequenceInput
): Promise<EmailSequence> {
  const response = await apiClient.post<ApiResponse<EmailSequence>>(BASE_URL, data);
  return response.data;
}

/**
 * Update a sequence.
 *
 * ⚠️ Passing `steps` REPLACES the whole list and re-numbers positions by array
 * index — there is no per-step endpoint. Existing enrolments are unaffected
 * either way: they hold their own snapshot taken at start time (D4).
 */
export async function updateEmailSequence(
  id: string,
  data: UpdateEmailSequenceInput
): Promise<EmailSequence> {
  const response = await apiClient.patch<ApiResponse<EmailSequence>>(`${BASE_URL}/${id}`, data);
  return response.data;
}

/**
 * Delete a sequence — or ARCHIVE it if it has ever been used.
 *
 * ⚠️ The result says which happened (`{ archived }`); it is not the deleted
 * sequence. A sequence with LIVE enrolments is refused with 409 rather than
 * either, because stopping those families is a separate, deliberate decision.
 */
export async function deleteEmailSequence(id: string): Promise<EmailSequenceDeleteResult> {
  const response = await apiClient.delete<ApiResponse<EmailSequenceDeleteResult>>(
    `${BASE_URL}/${id}`
  );
  return response.data;
}

// ===========================================
// PREVIEW & TEST
// ===========================================

/**
 * Render one step as a family would see it.
 *
 * Uses a fixed sample recipient server-side (never real family data). The
 * apostrophe in that sample is deliberate — it is what exposes double-escaping
 * in the preheader.
 */
export async function previewEmailSequenceStep(
  id: string,
  position: number
): Promise<EmailSequenceStepPreview> {
  const response = await apiClient.get<ApiResponse<EmailSequenceStepPreview>>(
    `${BASE_URL}/${id}/preview/${position}`
  );
  return response.data;
}

/**
 * Send one step to the logged-in admin.
 *
 * ⚠️ There is no recipient parameter — it always goes to the caller's own
 * address. Rate limited to 5 per 15 minutes, and that budget is SHARED with the
 * custom-email test endpoint.
 *
 * Returns the confirmation message; this route sends no `data`.
 */
export async function sendEmailSequenceTest(
  id: string,
  data: TestEmailSequenceInput
): Promise<string> {
  const response = await apiClient.post<ApiMessageOnlyResponse>(`${BASE_URL}/${id}/test`, data);
  return response.message;
}

// ===========================================
// ENROLMENTS
// ===========================================

export async function getSequenceEnrolments(id: string): Promise<EmailSequenceEnrolment[]> {
  const response = await apiClient.get<ApiResponse<EmailSequenceEnrolment[]>>(
    `${BASE_URL}/${id}/enrolments`
  );
  return response.data;
}

/**
 * Every series this student is or has been on — including stopped and finished
 * ones, deliberately: "why did this stop?" is the question an admin asks.
 */
export async function getStudentSequenceEnrolments(
  studentId: string
): Promise<EmailSequenceEnrolment[]> {
  const response = await apiClient.get<ApiResponse<EmailSequenceEnrolment[]>>(
    `${BASE_URL}/students/${studentId}/enrolments`
  );
  return response.data;
}

/**
 * What WOULD happen if this student were started now — every step with the day
 * it would actually go out, how many are already past, and who would receive it.
 *
 * Read-permission only, so a read-only admin can check before asking someone to
 * press the button. ⚠️ `recipients[].email` is omitted for those users.
 */
export async function previewSequenceStart(
  id: string,
  data: StartEmailSequencePreviewInput
): Promise<EmailSequenceStartPreview> {
  const response = await apiClient.post<ApiResponse<EmailSequenceStartPreview>>(
    `${BASE_URL}/${id}/start-preview`,
    data
  );
  return response.data;
}

export async function startSequenceForStudent(
  id: string,
  data: StartEmailSequenceInput
): Promise<EmailSequenceEnrolment> {
  const response = await apiClient.post<ApiResponse<EmailSequenceEnrolment>>(
    `${BASE_URL}/${id}/start`,
    data
  );
  return response.data;
}

/**
 * Pause a running series.
 *
 * ⚠️ Paused days are NOT made up. Steps that fall due while paused fire late,
 * one per school-day, once resumed — the schedule does not shift forward.
 */
export async function pauseSequenceEnrolment(
  enrolmentId: string
): Promise<EmailSequenceEnrolment> {
  const response = await apiClient.post<ApiResponse<EmailSequenceEnrolment>>(
    `${BASE_URL}/enrolments/${enrolmentId}/pause`
  );
  return response.data;
}

export async function resumeSequenceEnrolment(
  enrolmentId: string
): Promise<EmailSequenceEnrolment> {
  const response = await apiClient.post<ApiResponse<EmailSequenceEnrolment>>(
    `${BASE_URL}/enrolments/${enrolmentId}/resume`
  );
  return response.data;
}

/**
 * Stop a series permanently. Terminal — a returning student is a fresh
 * enrolment, never a resumed one. Emails already sent are unaffected.
 */
export async function cancelSequenceEnrolment(
  enrolmentId: string
): Promise<EmailSequenceEnrolment> {
  const response = await apiClient.post<ApiResponse<EmailSequenceEnrolment>>(
    `${BASE_URL}/enrolments/${enrolmentId}/cancel`
  );
  return response.data;
}
